import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Search, Edit, Trash2, Grid, List, BookOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { BookDetails } from "../../../services/types";
import { db } from "../../firebase/config";
  import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  startAt,
  endAt,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";

// ... existing imports ...
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { generateBookDetails } from "../../../services/geminiServices";

interface Book {
  id: string;
  title: string;
  author: string;
  isbn: string;
  category: string;
  shelf: string;
  quantity: number;
  available: number;
  status: "available" | "borrowed" | "reserved";
  coverImage?: string;
  description?: string;
  year?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

const BooksManagement = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [books, setBooks] = useState<Book[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  
  // Server-side Pagination State
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  // We'll use a simple page index and re-fetch concept or cursor stack.
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBooks, setTotalBooks] = useState(0);
  const itemsPerPage = 10;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: "",
    author: "",
    isbn: "",
    category: "",
    shelf: "",
    quantity: 1,
    description: "",
    year: ""
  });

  const [candidateBooks, setCandidateBooks] = useState<BookDetails[]>([]);
  const [showCandidateDialog, setShowCandidateDialog] = useState(false);

  const [bookCategories, setBookCategories] = useState<string[]>(["Fiction", "Non-Fiction", "Science", "History", "Biography", "Technology", "Arts"]);

  const [cover, setCover] = useState<string | undefined>(undefined);
  const [isCameraVisible, setIsCameraVisible] = useState(false);
  const [autoFillEnabled, setAutoFillEnabled] = useState(false);
  const [isGeneratingDetails, setIsGeneratingDetails] = useState(false);

  const [schoolID, setSchoolID] = useState<string | null>(null);

  // Helper to fetch total count (for accurate pagination numbers)
  const fetchTotalCount = async (schoolId: string, search: string) => {
      try {
        const booksCollectionRef = collection(db, `schools/${schoolId}/books`);
        let q = query(booksCollectionRef);
        // If searching, count is approximate or we skip (Firestore count with filters is supported but generic 'search' is hard)
        if (search) {
             // Exact count for search is expensive if we don't index.
             // We'll skip total count for search mode or just do a simple check.
             q = query(booksCollectionRef, orderBy("title"), startAt(search), endAt(search + '\uf8ff'));
        }
        const snapshot = await getCountFromServer(q);
        setTotalBooks(snapshot.data().count);
      } catch (err) {
        console.error("Error fetching count:", err);
      }
  };

  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeBooks: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        // ... (existing affiliation logic) ...
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;
        
        // Fallback: use top-level schoolID claim if no affiliation object was found
        const selectedSchoolID = selectedAffiliation?.schoolID || claimSchoolID || null;
        setSchoolID(selectedSchoolID);

        if (selectedSchoolID) {
           setLoading(true);
           // Fetch Initial Data
           fetchBooks(selectedSchoolID, searchTerm, null);
           fetchTotalCount(selectedSchoolID, searchTerm);
        } else {
            throw new Error("No school affiliation found.");
        }

      } catch (error: any) {
         // ... error handling ...
         console.error(error);
         setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth?.();
      if (unsubscribeBooks) unsubscribeBooks(); // We might switch to one-time gets for search or manage subscription reference
    };
  }, [navigate]); // Remove 'toast' from deps if causing loop, but keep if safe. 

  // Unified Fetch Function
  const fetchBooks = (sID: string, search: string, startAfterDoc: QueryDocumentSnapshot<DocumentData> | null) => {
    setLoading(true);
    const booksCollectionRef = collection(db, `schools/${sID}/books`);
    let q;

    if (search.trim()) {
        // Search Mode (Title Prefix)
        // Note: Firestore requires index for 'title'. Ensure user knows or it auto-creates.
        q = query(
            booksCollectionRef, 
            orderBy("title"), 
            startAt(search), 
            endAt(search + '\uf8ff'),
            limit(itemsPerPage)
        );
        if (startAfterDoc) {
             q = query(
                booksCollectionRef, 
                orderBy("title"), 
                startAt(search), 
                endAt(search + '\uf8ff'),
                startAfter(startAfterDoc),
                limit(itemsPerPage)
            );
        }
    } else {
        // Default Mode
        q = query(booksCollectionRef, orderBy("createdAt", "desc"), limit(itemsPerPage));
        if (startAfterDoc) {
            q = query(booksCollectionRef, orderBy("createdAt", "desc"), startAfter(startAfterDoc), limit(itemsPerPage));
        }
    }

    const unsub = onSnapshot(q, (snapshot) => {
        const booksData: Book[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
            updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt,
        })) as Book[];
        
        setBooks(booksData);
        if (snapshot.docs.length > 0) {
            setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        } else {
            // End of list?
        }
        setLoading(false);
    }, (err) => {
        console.error("Fetch error:", err);
        setError("Failed to fetch books.");
        setLoading(false);
    });
    
    // We need to store unsub to clean up if needed, but in this simplified fn inside component, 
    // strictly speaking we should manage it in a ref or effect. 
    // For now, we are relying on the main useEffect, but pagination clicks won't trigger that.
    // Better pattern: Put query params in state and have useEffect respond to them.
    return unsub; // Just returning it, but effect logic handles it better.
  };

  // Rewrite: Use Effect to listen to dependencies
  useEffect(() => {
      if (!schoolID) return;
      
      let unsubscribe: () => void;
      
      const load = async () => {
          setLoading(true);
          const booksCollectionRef = collection(db, `schools/${schoolID}/books`);
          let q;

          // Determine Base Query
          if (searchTerm.trim()) {
              q = query(booksCollectionRef, orderBy("title"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'));
          } else {
              q = query(booksCollectionRef, orderBy("createdAt", "desc"));
          }
          
          // Apply Pagination
          // Note: using 'pageStack' to determine startAfter point.
          // pageStack[0] is end of page 1, used for page 2 start.
          // pageStack[currentPage - 2] is end of (current - 1), used for start of current.
          // Wait, Page 1: no startAfter.
          // Page 2: startAfter(pageStack[0]).
          
          if (currentPage > 1 && pageStack.length >= currentPage - 1) {
              const cursor = pageStack[currentPage - 2];
              // q = query(q, startAfter(cursor), limit(itemsPerPage)); // Cant chain query like this easily with different orderBys
              // Reconstuct:
               if (searchTerm.trim()) {
                  q = query(booksCollectionRef, orderBy("title"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'), startAfter(cursor), limit(itemsPerPage));
               } else {
                  q = query(booksCollectionRef, orderBy("createdAt", "desc"), startAfter(cursor), limit(itemsPerPage));
               }
          } else {
               // Page 1
               if (searchTerm.trim()) {
                  q = query(booksCollectionRef, orderBy("title"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'), limit(itemsPerPage));
               } else {
                  q = query(booksCollectionRef, orderBy("createdAt", "desc"), limit(itemsPerPage));
               }
          }

          unsubscribe = onSnapshot(q, (snapshot) => {
              const booksData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : doc.data().createdAt,
                updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : doc.data().updatedAt,
              })) as Book[];
              
              setBooks(booksData);
              if (snapshot.docs.length > 0) {
                  setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
              } else {
                  setLastVisible(null); // No documents on this page
              }
              setLoading(false);
          }, (err) => {
            console.error(err);
            setError("Failed to load books");
            setLoading(false);
          });
          
          // Count update (debounce this in reality)
          fetchTotalCount(schoolID, searchTerm);
      };

      load();

      return () => {
          if (unsubscribe) unsubscribe();
      };
  }, [schoolID, searchTerm, currentPage]); // NOT pageStack (it changes on Next click, but Page should change first)
  
  // Handlers
  const handleNextPage = () => {
      if (books.length < itemsPerPage) return; // Can't go next if current page is not full
      if (lastVisible) {
          setPageStack(prev => [...prev, lastVisible]);
          setCurrentPage(prev => prev + 1);
      }
  };

  const handlePrevPage = () => {
      if (currentPage > 1) {
          setPageStack(prev => prev.slice(0, -1)); // Remove the last cursor
          setCurrentPage(prev => prev - 1);
      }
  };

  // Client-side category filter on the CURRENT server-fetched page 
  // (Ideally this should be server-side too but requires composite indexes with Title/Date)
  const displayBooks = books.filter(book => {
    return filterCategory === "all" || book.category === filterCategory;
  });

  // Calculate generic total pages if possible, else just show current
  // totalBooks is from server count
  const estimatedTotalPages = Math.ceil(totalBooks / itemsPerPage);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setPageStack([]); // Reset stack on new search
  };

  const handleCategoryChange = (value: string) => {
    setFilterCategory(value);
    // client side filter doesn't reset server page
  };

  const handleAddDialogOpen = () => {
    setEditingBook(null);
    setFormData({
      title: "",
      author: "",
      isbn: "",
      category: "",
      shelf: "",
      quantity: 1,
      description: "",
      year: ""
    });
    setCover(undefined);
    setIsCameraVisible(false);
    setAutoFillEnabled(false);
    setIsGeneratingDetails(false);
    setIsAddDialogOpen(true);
  };

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setCover(result);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleAutoFillDetails = async (checked: boolean) => {
    setAutoFillEnabled(checked);

    if (checked) {
      if (!formData.title && !formData.author && !formData.isbn) {
        toast({
          title: "Input Required",
          description: "Please provide a Title, Author, or ISBN to start AI search.",
          variant: "destructive",
        });
        setAutoFillEnabled(false);
        return;
      }

      setIsGeneratingDetails(true);
      
      try {
            console.log("Generating details for school:", schoolID);
            const candidates = await generateBookDetails(formData.title, formData.author, bookCategories, schoolID || undefined);

            if (!candidates || candidates.length === 0) {
              throw new Error("No books found.");
            }

            // If only one high-confidence match (or exact match logic could go here)
            // For now, we always show dialog if we have results to let user confirm, 
            // UNLESS there is exactly 1 and it's extremely close?
            // User requested: "if there are 2 similar books found it should show a nicely styled popup"
            // Let's just update the candidates and show the dialog to be safe and interactive.
            setCandidateBooks(candidates);
            setShowCandidateDialog(true);
            
      } catch (error: any) {
        console.error(error);
        toast({
          title: "AI Lookup Failed",
          description: error.message || 'Could not find book details.',
          variant: "destructive",
        });
        // Turn off toggle if failed
        setAutoFillEnabled(false);
      } finally {
        setIsGeneratingDetails(false);
      }
    }
  };

  const handleSelectCandidate = (book: BookDetails) => {
        setFormData(prev => ({
          ...prev,
          title: book.title || prev.title,
          author: book.author || prev.author,
          description: book.description || prev.description,
          year: book.yearPublished || prev.year,
        }));

        // Handle category
        const returnedCategory = book.category?.trim();
        if (returnedCategory && returnedCategory.length > 0) {
          const existingCategory = bookCategories.find(opt => opt.toLowerCase() === returnedCategory.toLowerCase());
          if (!existingCategory) {
            setBookCategories(prev => [...new Set([...prev, returnedCategory])]);
          }
          setFormData(prev => ({ ...prev, category: returnedCategory }));
        }

        setShowCandidateDialog(false);
        setAutoFillEnabled(false); // Reset toggle
        toast({
          title: "Book Details Auto-filled",
          description: "Please review and adjust if necessary.",
        });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.author) {
      toast({
        title: "Error",
        description: "Please fill in required fields",
        variant: "destructive",
      });
      return;
    }

    if (!schoolID) {
      toast({
        title: "Error",
        description: "School ID not found. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      const booksCollectionRef = collection(db, `schools/${schoolID}/books`);

      if (editingBook) {
        // use a flexible shape for updateData to allow optional coverImage and serverTimestamp
        let updateData: any = {
          ...formData,
          available: formData.quantity,
          updatedAt: serverTimestamp(),
        };
        if (cover !== undefined) {
          updateData.coverImage = cover;
        }
        const bookRef = doc(booksCollectionRef, editingBook.id);
        await updateDoc(bookRef, updateData);
        toast({
          title: "Success",
          description: "Book updated successfully",
        });
      } else {
        // allow coverImage to be added when present
        const bookData: any = {
          ...formData,
          available: formData.quantity,
          status: "available" as const,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (cover !== undefined) {
          bookData.coverImage = cover;
        }
        await addDoc(booksCollectionRef, bookData);
        toast({
          title: "Success",
          description: "Book added successfully",
        });
      }

      setFormData({
        title: "",
        author: "",
        isbn: "",
        category: "",
        shelf: "",
        quantity: 1,
        description: "",
        year: ""
      });
      setCover(undefined);
      setIsCameraVisible(false);
      setAutoFillEnabled(false);
      setIsGeneratingDetails(false);
      setIsAddDialogOpen(false);
      setEditingBook(null);
    } catch (error: any) {
      console.error("Error saving book:", error);
      toast({
        title: "Error",
        description: `Failed to save book: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (book: Book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      isbn: book.isbn,
      category: book.category,
      shelf: book.shelf,
      quantity: book.quantity,
      description: book.description || "",
      year: book.year || ""
    });
    setCover(book.coverImage);
    setIsCameraVisible(false);
    setAutoFillEnabled(false);
    setIsGeneratingDetails(false);
    setIsAddDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!schoolID) {
      toast({
        title: "Error",
        description: "School ID not found. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      const bookRef = doc(db, `schools/${schoolID}/books`, id);
      await deleteDoc(bookRef);
      toast({
        title: "Success",
        description: "Book deleted successfully",
      });
    } catch (error: any) {
      console.error("Error deleting book:", error);
      toast({
        title: "Error",
        description: `Failed to delete book: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    setBooks([]);
  };

  const getStatusBadge = (status: string, available: number, quantity: number) => {
    let badgeClass = "";
    let label = "";

    if (status === "available" && available === quantity) {
      badgeClass = "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate";
      label = "Available";
    } else if (status === "borrowed" || available === 0) {
      badgeClass = "bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate";
      label = "Not Available";
    } else if (status === "reserved") {
      badgeClass = "bg-[#3B82F6] dark:bg-[#60A5FA] hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate";
      label = "Reserved";
    } else {
      badgeClass = "bg-[#F59E0B] dark:bg-[#FBBF24] hover:bg-[#D97706] dark:hover:bg-[#F59E0B] text-white text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate";
      label = "Partially Available";
    }

    return <Badge className={badgeClass}>{label}</Badge>;
  };

  const CameraCapture: React.FC<{ onCapture: (dataUrl: string) => void; onCancel: () => void; }> = ({ onCapture, onCancel }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    useEffect(() => {
      const startCamera = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        } catch (err) {
          console.error("Error accessing camera:", err);
          toast({
            title: "Camera Error",
            description: "Could not access camera. Please ensure permissions are granted in your browser settings.",
            variant: "destructive",
          });
          onCancel();
        }
      };
      startCamera();

      return () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
    }, [onCancel, toast]);

    const handleCapture = () => {
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
          const dataUrl = canvas.toDataURL('image/jpeg');
          onCapture(dataUrl);
        }
      }
    };

    return (
      <div className="space-y-2">
        <video ref={videoRef} autoPlay playsInline className="w-full rounded-md border bg-muted"></video>
        <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        <div className="flex justify-between gap-2">
          <Button type="button" onClick={onCancel} variant="outline" className="w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">Cancel</Button>
          <Button type="button" onClick={handleCapture} className="w-full bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] transition-colors">Capture</Button>
        </div>
      </div>
    );
  };

  // Truncation function for Title, Author, and Category
  const truncateText = (text: string, maxLength: number = 30) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength - 3) + "...";
  };

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={handleRetry} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            .font-roboto {
              font-family: 'Roboto', sans-serif;
            }
            /* Mobile-only fix – prevents black overscroll bar */
            @media (max-width: 767px) {
              body { overflow-x: hidden; }
            }
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            /* Mobile-only full-width scroll container */
            @media (max-width: 767px) {
              .mobile-table-container {
                width: 100vw;
                margin-left: calc(-50vw + 50%);
                padding-left: 16px;
                padding-right: 16px;
                box-sizing: border-box;
              }

              /* ensure the table header's first cell aligns with the body cells */
              .mobile-table-container table thead th:first-child {
                padding-left: 16px;
              }
            }
            .tooltip-content {
              background-color: #FFFFFF !important;
              color: #000000 !important;
              border: 1px solid #E2E8F0 !important;
            }
            .dark .tooltip-content {
              background-color: #1E293B !important;
              color: #FFFFFF !important;
              border: 1px solid #334155 !important;
            }
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;  /* IE and Edge */
              scrollbar-width: none;  /* Firefox */
            }
          `}
        </style>
        <div className="w-full max-w-full min-h-0 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
          <div className="flex flex-col space-y-4 h-full min-h-0">
            <div className="flex flex-col gap-3 sm:gap-4 flex-shrink-0">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search books..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10 w-full bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                  />
                </div>
                <Select value={filterCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="w-full sm:w-40 md:w-48 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">All Categories</SelectItem>
                    {bookCategories.map(category => (
                      <SelectItem key={category} value={category} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-wrap gap-2 justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewMode(viewMode === "table" ? "grid" : "table")}
                  className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#1E293B] hover:text-[#2563EB] dark:hover:text-gray-300 font-medium flex-shrink-0"
                >
                  {viewMode === "table" ? <Grid className="h-4 w-4" /> : <List className="h-4 w-4" />}
                  <span className="ml-2 hidden sm:inline">
                    {viewMode === "table" ? "Grid" : "Table"}
                  </span>
                </Button>
                
                <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className="m-2 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6] flex-shrink-0 transition-colors"
                      onClick={handleAddDialogOpen}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Book
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                    <DialogHeader>
                      <div className="space-y-3">
                        <div className="flex justify-between items-start w-full">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold leading-none tracking-tight">
                              {editingBook ? "Edit Book" : "Add New Book"}
                            </h3>
                          </div>
                          {!editingBook && (
                            <div className="flex flex-col items-end gap-1">
                              <div className="flex items-center space-x-2">
                                <Switch 
                                  id="auto-fill-details"
                                  checked={autoFillEnabled}
                                  onCheckedChange={handleAutoFillDetails}
                                  disabled={isGeneratingDetails}
                                />
                                <label 
                                  htmlFor="auto-fill-details" 
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ml-2"
                                >
                                  {isGeneratingDetails ? "Generating..." : "Auto-fill details"}
                                </label>
                              </div>
                              <p className="text-xs text-amber-600 dark:text-amber-500 flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Be cautious: AI can make mistakes
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </DialogHeader>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({...formData, title: e.target.value})}
                          placeholder="Book title"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="author">Author *</Label>
                        <Input
                          id="author"
                          value={formData.author}
                          onChange={(e) => setFormData({...formData, author: e.target.value})}
                          placeholder="Author name"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="year">Year Published <span className="text-muted-foreground">(optional)</span></Label>
                        <Input
                          id="year"
                          value={formData.year}
                          onChange={(e) => setFormData({...formData, year: e.target.value})}
                          placeholder="YYYY"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="isbn">ISBN</Label>
                        <Input
                          id="isbn"
                          value={formData.isbn}
                          onChange={(e) => setFormData({...formData, isbn: e.target.value})}
                          placeholder="ISBN number"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})} disabled={isGeneratingDetails}>
                          <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                            {bookCategories.map(category => (
                              <SelectItem key={category} value={category} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{category}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shelf">Shelf Location</Label>
                        <Input
                          id="shelf"
                          value={formData.shelf}
                          onChange={(e) => setFormData({...formData, shelf: e.target.value})}
                          placeholder="e.g., A-1-5"
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quantity">Quantity</Label>
                        <Input
                          id="quantity"
                          type="number"
                          min="1"
                          value={formData.quantity}
                          onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          placeholder="Book description..."
                          rows={3}
                          className="bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6]"
                          disabled={isGeneratingDetails}
                        />
                      </div>
                      <div className="sm:col-span-2 space-y-2">
                        <Label>Book Cover</Label>
                        <div className="flex gap-2">
                          <Input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleCoverImageChange} 
                            className="flex-grow bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]" 
                            disabled={isCameraVisible || isGeneratingDetails}
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            onClick={() => setIsCameraVisible(true)} 
                            disabled={isCameraVisible || isGeneratingDetails}
                            className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
                          >
                            Take Picture
                          </Button>
                        </div>
                        <div className="mt-2 min-h-[12rem] border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-xl flex flex-col items-center justify-center bg-gray-50/50 dark:bg-gray-900/20 p-6 gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-900/40 group">
                          {isCameraVisible ? (
                            <CameraCapture 
                              onCapture={(dataUrl) => {
                                setCover(dataUrl);
                                setIsCameraVisible(false);
                              }}
                              onCancel={() => setIsCameraVisible(false)}
                            />
                          ) : cover ? (
                            <div className="relative group w-full h-full flex justify-center">
                                <img src={cover} alt="Cover preview" className="max-h-48 w-auto object-contain rounded-md shadow-sm" />
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
                              <div className="p-4 rounded-full bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 group-hover:scale-110 transition-transform duration-300">
                                <BookOpen className="w-8 h-8 text-blue-500 dark:text-blue-400" />
                              </div>
                              <div className="text-center space-y-1">
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No cover selected</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">Upload an image or take a photo</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button onClick={handleSubmit} className="flex-1 bg-[#2563EB] hover:bg-[#1E40AF] dark:hover:bg-[#3B82F6]" disabled={isGeneratingDetails}>
                        {editingBook ? "Update Book" : "Add Book"}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setIsAddDialogOpen(false);
                          setEditingBook(null);
                          setFormData({
                            title: "",
                            author: "",
                            isbn: "",
                            category: "",
                            shelf: "",
                            quantity: 1,
                            description: "",
                            year: ""
                          });
                          setCover(undefined);
                          setIsCameraVisible(false);
                          setAutoFillEnabled(false);
                          setIsGeneratingDetails(false);
                        }}
                        className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]"
                        disabled={isGeneratingDetails}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Candidate Selection Dialog */}
            <Dialog open={showCandidateDialog} onOpenChange={setShowCandidateDialog}>
              <DialogContent className="max-w-2xl bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] shadow-xl p-0 overflow-hidden">
                <DialogHeader className="p-6 pb-4 border-b border-[#F1F5F9] dark:border-[#1E293B]">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#2563EB]/10 dark:bg-[#2563EB]/20">
                      <BookOpen className="w-5 h-5 text-[#2563EB]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select the Correct Book</h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        We found {candidateBooks.length} possible match{candidateBooks.length !== 1 ? 'es' : ''}. Select the correct one.
                      </p>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="p-4 max-h-[55vh] overflow-y-auto">
                  <div className="space-y-3">
                    {candidateBooks.map((book, idx) => (
                      <div 
                        key={idx} 
                        className="group relative bg-[#FFFFFF] dark:bg-[#0A1628] rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] p-4 m-2 hover:border-[#2563EB] dark:hover:border-[#3B82F6] hover:shadow-sm transition-all duration-200 cursor-pointer"
                        onClick={() => handleSelectCandidate(book)}
                      >
                         <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-[#2563EB] dark:group-hover:text-[#60A5FA] transition-colors line-clamp-1">
                                  {book.title}
                                </h3>
                                <Badge className="shrink-0 text-xs bg-[#E0F2FE] dark:bg-[#1E293B] text-[#0369A1] dark:text-[#38BDF8] border-0">
                                  {book.category}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 mb-2">
                                <span className="font-medium">{book.author}</span>
                                <span className="text-gray-300 dark:text-gray-600">|</span>
                                <span>{book.yearPublished || 'Year Unknown'}</span>
                              </div>
                              <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
                                {book.description}
                              </p>
                            </div>
                            <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                         </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 bg-[#F8FAFC] dark:bg-[#0A1628] border-t border-[#F1F5F9] dark:border-[#1E293B] flex justify-end">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowCandidateDialog(false)}
                    className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                  >
                    Cancel
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="flex-1 min-h-0">
              {viewMode === "table" ? (
                <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <CardContent className="p-0 flex-1 min-h-0">
                    <div className="h-full overflow-auto no-scrollbar">
                      <div className="mobile-table-container overflow-x-auto">
                        <Table className="w-full table-fixed">
                        <TableHeader>
                          <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                            <TableHead className="w-[30%]">Title</TableHead>
                            <TableHead className="w-[20%] hidden sm:table-cell">Author</TableHead>
                            <TableHead className="w-[15%] hidden md:table-cell">Category</TableHead>
                            <TableHead className="w-[10%] hidden lg:table-cell">Shelf</TableHead>
                            <TableHead className="w-[15%]">Status</TableHead>
                            <TableHead className="w-[7%] hidden sm:table-cell pr-4">Qty</TableHead>
                            <TableHead className="w-[13%] text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {displayBooks.map((book: Book) => (
                            <TableRow key={book.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                              <TableCell className="font-medium pr-2 py-2">
                                <div className="min-w-0">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="truncate max-w-[250px]">{truncateText(book.title, 30)}</div>
                                    </TooltipTrigger>
                                    <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                                      <p className="text-sm">{book.title}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <div className="text-xs text-muted-foreground sm:hidden">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="truncate max-w-[150px]">{truncateText(book.author, 20)}</div>
                                      </TooltipTrigger>
                                      <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                                        <p className="text-sm">{book.author}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell min-w-0 py-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="truncate max-w-[150px]">{truncateText(book.author, 20)}</div>
                                  </TooltipTrigger>
                                  <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                                    <p className="text-sm">{book.author}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TableCell>
                              <TableCell className="hidden md:table-cell min-w-0 py-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="truncate max-w-[150px]">{truncateText(book.category, 20)}</div>
                                  </TooltipTrigger>
                                  <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                                    <p className="text-sm">{book.category}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TableCell>
                              <TableCell className="hidden lg:table-cell py-2">
                                <div>{book.shelf}</div>
                              </TableCell>
                              <TableCell className="py-2">{getStatusBadge(book.status, book.available, book.quantity)}</TableCell>
                              <TableCell className="hidden sm:table-cell text-center py-2 pr-4">{book.available}/{book.quantity}</TableCell>
                              <TableCell className="text-right py-2">
                                <div className="flex gap-0.5 justify-end">
                                  <Button variant="ghost" size="sm" onClick={() => handleEdit(book)} className="h-7 w-7 p-0 my-2">
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(book.id)} className="h-7 w-7 p-0 my-2 text-destructive hover:text-destructive">
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="h-full overflow-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {displayBooks.map((book: Book) => (
                      <Card key={book.id} className="h-fit bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                        <CardHeader className="pb-3">
                          <div className="aspect-[3/4] bg-[#F1F5F9] dark:bg-[#1E293B] rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                            {book.coverImage ? (
                              <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" />
                            ) : (
                              <BookOpen className="h-12 w-12 text-muted-foreground" />
                            )}
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <CardTitle className="text-base leading-tight line-clamp-2">{book.title}</CardTitle>
                            </TooltipTrigger>
                            <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                              <p className="text-sm">{book.title}</p>
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p className="text-sm text-muted-foreground line-clamp-1">by {book.author}</p>
                            </TooltipTrigger>
                            <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                              <p className="text-sm">by {book.author}</p>
                            </TooltipContent>
                          </Tooltip>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">Category:</span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs truncate ml-2 max-w-[150px]">{truncateText(book.category, 20)}</span>
                                </TooltipTrigger>
                                <TooltipContent className="tooltip-content p-2 rounded-md shadow-lg">
                                  <p className="text-sm">{book.category}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">Shelf:</span>
                              <span className="text-xs ml-2">{book.shelf}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">Available:</span>
                              <span className="text-xs">{book.available}/{book.quantity}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-xs text-muted-foreground">Status:</span>
                              {getStatusBadge(book.status, book.available, book.quantity)}
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={() => handleEdit(book)} className="flex-1 my-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white hover:bg-gray-50 dark:hover:bg-[#131E31]">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="destructive" size="sm" onClick={() => handleDelete(book.id)} className="flex-1 my-2">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B] mb-4">
              <div className="text-sm text-muted-foreground text-center">
                Page {currentPage} {totalBooks > 0 ? `of ~${estimatedTotalPages}` : ''} ({displayBooks.length} shown)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
                  {currentPage}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={books.length < itemsPerPage}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>

            {displayBooks.length === 0 && (
              <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                <CardContent className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground">No books found</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {searchTerm || filterCategory !== "all" ? "Try adjusting your search or filters" : "Start by adding your first book"}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </>
    </TooltipProvider>
  );
};

export { BooksManagement };